import { isEmpty } from 'lodash';
import posthog from 'posthog-js';
import { POSTHOG_HOST, POSTHOG_KEY, POSTHOG_UI_HOST } from 'common/config/app';
import type { User } from 'common/types/store';

// Guard so posthog.init only runs once even though the provider effect re-runs on user changes.
let initialized = false;

const getEmailDomain = (address: string): string => {
  const lastIndex = address.lastIndexOf('@');
  return address.slice(lastIndex + 1);
};

// Initializes PostHog (product analytics + session replay) and identifies the current user.
// Session recording is enabled by default; it still requires the project-level
// "Record user sessions" toggle to be on in PostHog.
export const initPostHog = (user: User | null): void => {
  if (!POSTHOG_KEY) return;

  if (!initialized) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST, // api_host is our reverse proxy; keep generated links (toolbar, etc.) pointed at real PostHog
      capture_pageview: true,
      disable_session_recording: false, // session replay ON
      capture_heatmaps: true, // heatmaps ($heatmap_data) ON; clickmaps use autocapture (on by default)
      capture_exceptions: true, // frontend error tracking ($exception) ON — links to the session automatically
    });
    initialized = true;
    // Module import does not attach to window; expose the instance for debugging/console access.
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
  }

  // Consent may have been revoked in a previous render; re-enable capture when allowed.
  posthog.opt_in_capturing();

  if (user && !isEmpty(user)) {
    posthog.identify(String(user.id), {
      user_email_domain: user.email ? getEmailDomain(user.email) : '',
      is_superuser: user.is_superuser,
    });
  }
};

// Stops all capturing (including session replay) when analytics consent is not granted.
export const stopPostHog = (): void => {
  if (!POSTHOG_KEY || !initialized) return;
  posthog.opt_out_capturing();
  posthog.stopSessionRecording();
};

// Headers that link a backend request to the current browser session + person. The Django
// PosthogContextMiddleware reads these (X-POSTHOG-SESSION-ID / X-POSTHOG-DISTINCT-ID) and tags
// every server-side event/exception/log with them, so backend errors resolve to a replay + user.
export const getPostHogTrackingHeaders = (): Record<string, string> => {
  if (!POSTHOG_KEY || !initialized) return {};

  const headers: Record<string, string> = {};
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();
  if (sessionId) headers['X-POSTHOG-SESSION-ID'] = sessionId;
  if (distinctId) headers['X-POSTHOG-DISTINCT-ID'] = distinctId;
  return headers;
};
